<template>
  <div>
    <svg
      id="map"
      ref="map"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      version="1.1"
      x="0px"
      y="0px"
      :viewBox="vBox"
      style="enable-background: new 0 0 5800 3000"
      xml:space="preserve"
    >
      <g id="countries">
        <Country
          v-for="country in countryPathsArray"
          :key="country.id"
          :id="country.id"
          :bbox="country.bbox"
          :paths="country.paths"
          @mouse-over="hoveredCountryId = country.id"
          @mouse-out="hoveredCountryId = null"
          @click="countryClicked(country.id)"
          @dblclick="countryDoubleClicked(country.id)"
        />
      </g>
      <g id="borders">
        <Border v-for="(border, i) in borderPaths" :key="i" :d="border" />
      </g>
      <g id="projectiles">
        <Projectiles />
      </g>
    </svg>
    <MapControlls />
    <div class="country-name">
      {{ countryName }}
    </div>
  </div>
</template>

<script>
import { SVG } from '@svgdotjs/svg.js';
import '@svgdotjs/svg.panzoom.js';
import { createNamespacedHelpers, mapGetters } from 'vuex';

import Country from 'Components/NewCountryPath';
import Border from 'Components/CountryBorder';
import Projectiles from 'Components/Projectiles';
import MapControlls from './MapControll.vue';

const {
  mapState: mapMapStoreState,
  mapMutations: mapMapStoreMutations,
  mapGetters: mapMapStoreGetters
} = createNamespacedHelpers('mapStore');

export default {
  name: 'SVGMap',
  components: { Country, Border, Projectiles, MapControlls },
  data: () => {
    return {
      hoveredCountryId: null
    };
  },
  computed: {
    ...mapGetters({ events: 'eventStore/attacksByTimestamp' }),
    ...mapMapStoreState({
      countryPathsArray: (state) => state.countries.paths,
      borderPaths: (state) => state.countries.borders,
      svg: (state) => state.svg,
      currentVBox: (state) => state.svg.currentVBox,
      activeCountryId: (state) => state.countries.activeCountryId
    }),
    ...mapMapStoreGetters(['getCIdById', 'getActiveCountryDetail', 'getCountryDetailById']),
    vBox() {
      return `${this.currentVBox.x} ${this.currentVBox.y} ${this.currentVBox.w} ${this.currentVBox.h}`;
    },
    countryName() {
      if (!this.hoveredCountryId) return '';

      const cId = this.getCIdById(this.hoveredCountryId);

      if (cId) {
        return this.$t(`countries.${cId}.ctry`);
      } else {
        return this.getCountryDetailById(this.hoveredCountryId)?.ctry;
      }
    }
  },
  methods: {
    ...mapMapStoreMutations([
      'setMapLoadingState',
      'setSVGParentEl',
      'setSVGParentObj',
      'setMinZoom',
      'setZoom',
      'setVBox',
      'setActiveCountryId'
    ]),
    onPanningHandler(e) {
      this.setVBox(e.detail.box);
    },
    onZoomingHandler(e) {
      const {
        detail: { level, focus }
      } = e;

      this.setZoom(level);

      this.svg.parentObj.zoom(level, focus);

      this.setVBox(this.svg.parentObj.viewbox());
    },
    countryClicked(id) {
      // currently unused
      // console.log(id);
    },
    countryDoubleClicked(id) {
      // currently unused
      // console.log(id);
    }
  },
  mounted() {
    // we don't have to ensure all the child components
    // (the countires) to be mounted, since all the initalizations
    // we do here is based on the base SVG element (parent/canvas)
    const mapCanvas = SVG('#map');

    const defaultZoomLevel = mapCanvas.zoom();
    const zoomMin = defaultZoomLevel;
    const zoomMax = this.svg.maxZoom;
    const zoomFactor = this.svg.zoomFactor;

    this.setSVGParentEl(this.$refs.map);
    this.setSVGParentObj(mapCanvas);
    this.setMinZoom(zoomMin);
    this.setZoom(defaultZoomLevel);

    /**
     * For now panzoom for svgjs is broken.
     * https://github.com/svgdotjs/svg.panzoom.js/pull/86/commits
     * A fix as a pull request is already sent, but not merged yet,
     * should uncomment the below lines and use the fixed code
     * when it is merged.
     */

    // init panZoom
    mapCanvas.panZoom({
      panning: true,
      zoomMin,
      zoomMax,
      zoomFactor
    });

    // set up zooming and panning handlers
    mapCanvas.on('panning', this.onPanningHandler);
    mapCanvas.on('zoom', this.onZoomingHandler);

    // but we wait for the whole render of child components
    // to finish before mutating the state
    this.$nextTick(function () {
      this.setMapLoadingState(false);
    });
  }
};
</script>

<style lang="scss" scoped>
@import 'Plugins/style/breakpoints';

#map {
  width: 100%;
  height: 100vh;
  padding-bottom: 2em;
  @media (min-width: $phone) and (max-width: $tablet) {
  }

  @media (min-width: $tablet) and (max-width: $desktop) {
  }

  @media (min-width: $desktop) {
    width: 100vw;
  }
}

.country-name {
  position: absolute;
  bottom: 24px;
  right: 50%;
  font-size: 1em;
  transform: translate(50%, 0);
}
</style>
